import notification from "antd/es/notification";
import type Calendar from "nylas/lib/models/calendar";
import { defineMessages } from "react-intl";
import { all, call, put, select, takeLatest, takeLeading } from "redux-saga/effects";

import NylasType from "@mapmycustomers/shared/enum/NylasType";
import { showModal } from "@mapmycustomers/ui";

import NylasFolder from "@app/types/nylas/NylasFolder";
import NylasInfo, { CreateNylasEventPayload } from "@app/types/nylas/NylasInfo";
import getValidationErrors from "@app/util/errorHandling/getValidationErrors";

import i18nService from "../../config/I18nService";
import { callApi } from "../api/callApi";
import { handleError } from "../errors/actions";

import {
  createCalendarEventForDealClosingDate,
  createNylas,
  deleteNylas,
  fetchCalendars,
  fetchNylasFolders,
  fetchNylasInfo,
  initializeNylas,
  updateNylas,
} from "./actions";
import { getNylasCalendarInfo } from "./selectors";

const messages = defineMessages({
  emailAlreadyInUseModalContent: {
    id: "nylas.emailAlreadyInUseModal.content",
    defaultMessage:
      "The email address you’re trying to connect is already in use on another account. Please use a different email address.",
    description: "Nylas email already in use modal content",
  },
  emailAlreadyInUseModalOkButton: {
    id: "nylas.emailAlreadyInUseModal.okButton",
    defaultMessage: "Okay",
    description: "Nylas email already in use modal ok button",
  },
  emailAlreadyInUseModalTitle: {
    id: "nylas.emailAlreadyInUseModal.title",
    defaultMessage: "Email address already in use",
    description: "Nylas email already in use modal title",
  },
  successfullyCreatedEventForDealClosingDateMessage: {
    id: "nylas.createEventForDealClosingDate.success",
    defaultMessage: "Event created",
    description: "Event was successfully created for the given deal",
  },
});

function* handleNylasError(error: unknown) {
  let displayNotification = true;

  const intl = i18nService.getIntl();
  const validationError = getValidationErrors(error)[0];
  if (intl && validationError?.code === "v0909") {
    showModal({
      centered: true,
      closable: true,
      content: intl.formatMessage(messages.emailAlreadyInUseModalContent),
      okButtonProps: {
        type: "primary",
      },
      okText: intl.formatMessage(messages.emailAlreadyInUseModalOkButton),
      title: intl.formatMessage(messages.emailAlreadyInUseModalTitle),
      type: "error",
      width: "30rem",
    });
    displayNotification = false;
  }

  yield put(handleError({ displayNotification, error }));
}

export function* onInitializeNylas() {
  try {
    const [nylasCalendarInfo, nylasEmailInfo]: ["" | NylasInfo, "" | NylasInfo] = yield all([
      callApi("fetchNylasInfo", NylasType.CALENDAR),
      callApi("fetchNylasInfo", NylasType.EMAIL),
    ]);
    yield put(
      initializeNylas.success({
        nylasCalendarInfo: nylasCalendarInfo || undefined,
        nylasEmailInfo: nylasEmailInfo || undefined,
      })
    );
  } catch (error) {
    yield put(initializeNylas.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchNylasInfo({ payload: type }: ReturnType<typeof fetchNylasInfo.request>) {
  try {
    const nylasInfo: "" | NylasInfo = yield callApi("fetchNylasInfo", type);
    yield put(fetchNylasInfo.success({ nylasInfo: nylasInfo || undefined, type }));
  } catch (error) {
    yield put(fetchNylasInfo.failure());
    yield put(handleError({ error }));
  }
}

export function* onCreateNylas({
  payload: { callback, nylas },
}: ReturnType<typeof createNylas.request>) {
  try {
    const response: NylasInfo = yield callApi("createNylas", nylas);
    callback?.(response);
    yield put(createNylas.success());
    yield put(initializeNylas.request());
  } catch (error) {
    yield put(createNylas.failure());
    yield call(handleNylasError, error);
  }
}

export function* onUpdateNylas({
  payload: { callback, nylas },
}: ReturnType<typeof updateNylas.request>) {
  try {
    const response: NylasInfo = yield callApi("updateNylas", nylas);
    callback?.(response);
    yield put(updateNylas.success(response));
    yield put(initializeNylas.request());
  } catch (error) {
    yield put(updateNylas.failure());
    yield call(handleNylasError, error);
  }
}

export function* onDeleteNylas({
  payload: { callback, nylasId, type },
}: ReturnType<typeof deleteNylas.request>) {
  try {
    yield callApi("deleteNylas", nylasId);
    callback?.();
    yield put(deleteNylas.success(type));
  } catch (error) {
    yield put(deleteNylas.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchNylasFolders() {
  try {
    const folders: NylasFolder[] = yield callApi("fetchNylasFolders");
    yield put(fetchNylasFolders.success(folders));
  } catch (error) {
    yield put(fetchNylasFolders.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchCalendars() {
  try {
    const nylasInfo: NylasInfo = yield select(getNylasCalendarInfo);
    const calendars: Calendar[] = yield callApi("fetchCalendars", nylasInfo.id);
    yield put(fetchCalendars.success(calendars));
  } catch (error) {
    yield put(fetchCalendars.failure());
    yield put(handleError({ error }));
  }
}

export function* onCreateCalendarEventForDealClosingDate({
  payload,
}: ReturnType<typeof createCalendarEventForDealClosingDate.request>) {
  try {
    const { callback, deal, eventDateTime } = payload;

    const nylasInfo: NylasInfo = yield select(getNylasCalendarInfo);

    const requestPayload: CreateNylasEventPayload = {
      closingDate: eventDateTime.toISOString(),
      name: `"${deal.name}" Deal close date`,
    };

    yield callApi("createNylasEvent", nylasInfo.id, requestPayload);

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.successfullyCreatedEventForDealClosingDateMessage),
      });
    }
    yield put(createCalendarEventForDealClosingDate.success());
    if (callback) {
      callback();
    }
  } catch (error) {
    yield put(createCalendarEventForDealClosingDate.failure());
    yield put(handleError({ error }));
  }
}

export function* nylasSaga() {
  yield takeLeading(initializeNylas.request, onInitializeNylas);
  yield takeLeading(createNylas.request, onCreateNylas);
  yield takeLeading(deleteNylas.request, onDeleteNylas);
  yield takeLeading(updateNylas.request, onUpdateNylas);
  yield takeLatest(fetchCalendars.request, onFetchCalendars);
  yield takeLatest(fetchNylasFolders.request, onFetchNylasFolders);
  yield takeLatest(fetchNylasInfo.request, onFetchNylasInfo);
  yield takeLatest(
    createCalendarEventForDealClosingDate.request,
    onCreateCalendarEventForDealClosingDate
  );
}
